import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../api/api";
import SearchBar from "../../ReusableComponents/Search/SearchBar";
import CustomSnackbar from "../../components/CustomSnackbar";
import en_ja from "../../translations/en_ja";
import CustomTableServer from "../../ReusableComponents/Table/CustomTableServer";

export default function UserManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    getAllUsers(page + 1);
  }, [page]);

  const getAllUsers = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/admin/get/users?page=${pageNum}`);
      if (res?.data?.data) {
        setUserData(res.data.data);
        setTotalCount(res.data.meta?.total || 0);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setUserToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        await api.delete(`/v1/auth/delete/user/${userToDelete.id}`);

        setDeleteConfirmOpen(false);
        setUserToDelete(null);
        setSnackbarMessage(en_ja["User deleted successfully"]);
        setSnackbarType("success");
        setSnackbarOpen(true);
        getAllUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        // Handle error (show error message to user)
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  };

  // const filteredRows = userData.filter((row) =>
  //   [row.name, row.email, row.phone_number].join(" ").toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const columns = [
    { id: "name", label: "Name", sortable: true, },
    { id: "email", label: "Email", sortable: true, },
    { id: "phone_number", label: "Contact", sortable: true, },
    {
      id: "gender",
      label: "Gender",
      sortable: true,
      render: (row) => {
        if (row.gender === 0 || row.gender === "0") {
          return en_ja["Male"];
        } else if (row.gender === 1 || row.gender === "1") {
          return en_ja["Female"];
        } else {
          return en_ja["-"];
        }
      },
    },
    {
      id: "actions",
      label: "Actions",
      render: (row) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(row)}
          sx={{
            color: "error.main",
            "&:hover": { backgroundColor: "error.light", color: "error.dark" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <div>
      <CustomSnackbar
        isOpen={snackbarOpen}
        type={snackbarType}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      <Box sx={{ my: 3 }}>
        <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </Box>

      <Card
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #e9ecef",
            backgroundColor: "white",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": { boxShadow: "0 12px 32px rgba(0,0,0,0.12)", transform: "translateY(-4px)" },
          }}
        >

          <CustomTableServer
            columns={columns}
            rows={userData}
            loading={loading}
            searchTerm={searchTerm}
            page={page}
            rowsPerPage={100}
            totalCount={totalCount}
            onPageChange={(e, newPage) => setPage(newPage)}
          />

        </Paper>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
          {en_ja["Confirm Delete"]}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {en_ja["Are you sure you want to delete the user"]}{" "}
            <strong>{userToDelete?.name}</strong>?{" "}
            {en_ja["This action cannot be undone."]}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            color="primary"
          >
            {en_ja["Cancel"]}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
          >
            {en_ja["Delete"]}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
